// Copyright © 2025 The Things Industries B.V.

import React, { useCallback, useMemo, useState } from 'react'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { defineMessages } from 'react-intl'

import { IconTag } from '@ttn-lw/components/icon'
import Button from '@ttn-lw/components/button'
import Input from '@ttn-lw/components/input'
import Checkbox from '@ttn-lw/components/checkbox'

import Message from '@ttn-lw/lib/components/message'

import Pill from '@console/components/pill/index.tti'

import PropTypes from '@ttn-lw/lib/prop-types'
import sharedMessages from '@ttn-lw/lib/shared-messages'

import { selectLabels } from '@console/store/selectors/labels.tti'

import style from './filter-by-label-button.tti.styl'

const m = defineMessages({
  filterLabelsWithFilter: 'Labels ({value})',
  filterByLabel: 'Filter by label',
})

const FilterByLabelButton = ({ hasLabelFilter, setHasLabelFilter, setLabelToFilter }) => {
  const labelsList = useSelector(selectLabels)
  const [searchLabelValue, setSearchLabelValue] = useState('')
  const [labelList, setLabelList] = useState(labelsList)
  const labelsValues = useMemo(
    () =>
      labelsList.reduce((acc, label) => {
        acc[label.ids.label_id] = false

        return acc
      }, {}),
    [labelsList],
  )
  const onLabelFilterChange = useCallback(
    value => {
      setLabelFilterValues(value)
      setLabelToFilter(Object.keys(value).filter(key => value[key]))
      setHasLabelFilter(Object.values(value).some(Boolean))
    },
    [setHasLabelFilter, setLabelToFilter],
  )
  const [labelFilterValues, setLabelFilterValues] = React.useState(labelsValues)

  const searchLabel = useCallback(
    value => {
      const newLabelList = labelsList.filter(label =>
        label.name.toLowerCase().includes(value.toLowerCase()),
      )
      const newLabelFilterValues = newLabelList.reduce((acc, label) => {
        acc[label.ids.label_id] = false

        return acc
      }, {})

      setLabelFilterValues(newLabelFilterValues)
      setLabelList(newLabelList)
      setSearchLabelValue(value)
    },
    [labelsList],
  )

  const handleClearFilters = useCallback(() => {
    setLabelFilterValues(labelsValues)
    setSearchLabelValue('')
    setLabelList(labelsList)
    setHasLabelFilter(false)
  }, [labelsValues, labelsList, setHasLabelFilter])

  const dropdownItems = (
    <>
      <div className="d-flex j-between al-center">
        <Message content={m.filterByLabel} component="h4" className="m-vert-0 p-cs-xs" />
        <Button naked small message={sharedMessages.clear} onClick={handleClearFilters} />
      </div>
      <Input
        className="mb-cs-xs"
        placeholder={sharedMessages.searchLabel}
        value={searchLabelValue}
        onChange={searchLabel}
      />
      <Checkbox.Group
        name="label_ids"
        value={labelFilterValues}
        onChange={onLabelFilterChange}
        className={classNames(style.filterCheckboxGroup, 'flex-no-wrap')}
      >
        {labelList.map(label => (
          <Checkbox
            className="p-cs-xs m-0 gap-cs-xs"
            key={label.ids.label_id}
            name={label.ids.label_id}
            label=""
          >
            <Pill color={label.color} text={label.name} />
          </Checkbox>
        ))}
      </Checkbox.Group>
    </>
  )

  return (
    <Button
      icon={IconTag}
      className={classNames({
        [style.labelFilterButton]: hasLabelFilter,
      })}
      secondary
      dropdownItems={dropdownItems}
      message={hasLabelFilter ? m.filterLabelsWithFilter : sharedMessages.labels}
      messageValues={{
        value: Object.values(labelFilterValues).filter(Boolean).length,
      }}
      preventDefault
    />
  )
}

FilterByLabelButton.propTypes = {
  hasLabelFilter: PropTypes.bool.isRequired,
  setHasLabelFilter: PropTypes.func.isRequired,
  setLabelToFilter: PropTypes.func.isRequired,
}

export default FilterByLabelButton
